import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import type { ApiError } from '@/types'

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

// Upload subdomain disabilitato (CF proxy ON → SSL fail). Route via main domain.
// Per file >100MB riattivare DNS-only su Cloudflare e rimettere 'https://upload.jarvis-erp.e-quipe.app/api/v1'
const UPLOAD_BASE_URL = BASE_URL

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Flag to prevent refresh loop
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

// ============================================================
// REQUEST INTERCEPTOR: add Bearer token
// ============================================================
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ============================================================
// RESPONSE INTERCEPTOR: handle 401 with token refresh
// ============================================================
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Auto-unwrap backend envelope { success: true, data: ... }
    if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
      response.data = response.data.data
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve) => {
          addRefreshSubscriber((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(axiosInstance(originalRequest))
          })
        })
      }

      isRefreshing = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) throw new Error('No refresh token')

        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken })
        const newAccessToken: string = data.data?.accessToken ?? data.accessToken
        const newRefreshToken: string = data.data?.refreshToken ?? data.refreshToken

        localStorage.setItem('accessToken', newAccessToken)
        localStorage.setItem('refreshToken', newRefreshToken)

        // Also sync to Zustand store if available
        try {
          const { useAuthStore } = await import('@/stores/authStore')
          const store = useAuthStore.getState()
          if (store.tokens) {
            store.setTokens({ ...store.tokens, accessToken: newAccessToken, refreshToken: newRefreshToken })
          }
        } catch {
          // store not available, localStorage sync is sufficient
        }

        onRefreshed(newAccessToken)
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return axiosInstance(originalRequest)
      } catch {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')

        try {
          const { useAuthStore } = await import('@/stores/authStore')
          useAuthStore.getState().logout()
        } catch {
          // ignore
        }

        window.location.href = '/login'
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// ============================================================
// TYPED API CLIENT
// ============================================================

class ApiClient {
  private instance: AxiosInstance

  constructor(instance: AxiosInstance) {
    this.instance = instance
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.instance.get<T>(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.post<T>(url, data)
    return response.data
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.put<T>(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.instance.patch<T>(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.instance.delete<T>(url)
    return response.data
  }
}

export const apiClient = new ApiClient(axiosInstance)

/**
 * Tenta il refresh del token JWT e ritorna il nuovo accessToken.
 * Da usare in contesti dove non si usa Axios (es. fetch SSE).
 * Lancia errore se il refresh fallisce (redirect a login necessario).
 */
export async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) throw new Error('No refresh token')

  const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken })
  const newAccessToken: string = data.data?.accessToken ?? data.accessToken
  const newRefreshToken: string = data.data?.refreshToken ?? data.refreshToken

  localStorage.setItem('accessToken', newAccessToken)
  localStorage.setItem('refreshToken', newRefreshToken)

  try {
    const { useAuthStore } = await import('@/stores/authStore')
    const store = useAuthStore.getState()
    if (store.tokens) {
      store.setTokens({ ...store.tokens, accessToken: newAccessToken, refreshToken: newRefreshToken })
    }
  } catch {
    // store not available
  }

  return newAccessToken
}

export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined
    // Backend format: { error: { code, message } }
    const nested = data?.error as Record<string, unknown> | undefined
    if (typeof nested?.message === 'string') return nested.message
    // Flat format: { message }
    if (typeof data?.message === 'string') return data.message
    return error.message ?? 'Errore sconosciuto'
  }
  if (error instanceof Error) return error.message
  return 'Errore sconosciuto'
}

// Prisma serializza i campi Decimal come stringa in JSON (es. { amountGross: "15.4" }).
// I campi tipizzati "number" nei tipi frontend (FicInvoice, FicReconciliation, ecc.) possono
// quindi arrivare a runtime come stringa: usare qui, MAI sommarli/ordinarli grezzi con `+`.
export function toNum(value: unknown): number {
  if (typeof value === 'number') return value
  const n = parseFloat(String(value))
  return Number.isFinite(n) ? n : 0
}

// ============================================================
// NAMED API GROUPS
// ============================================================

export const authApi = {
  login: (email: string, password: string) =>
    axiosInstance.post('/auth/login', { email, password }),
  refresh: (refreshToken: string) =>
    axiosInstance.post('/auth/refresh', { refreshToken }),
  logout: () =>
    axiosInstance.post('/auth/logout'),
  me: () =>
    axiosInstance.get('/auth/me'),
  changePassword: (data: { oldPassword: string; newPassword: string }) =>
    axiosInstance.post('/auth/change-password', data),
}

export const apiKeysApi = {
  list: () => axiosInstance.get('/api-keys'),
  create: (label: string) => axiosInstance.post('/api-keys', { label }),
  revoke: (id: string) => axiosInstance.delete(`/api-keys/${id}`),
}

export const clientsApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/clients', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/clients/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/clients', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/clients/${id}`, data),
  delete: (id: string) =>
    axiosInstance.delete(`/clients/${id}`),
  // Contacts sub-resource
  getContacts: (clientId: string) =>
    axiosInstance.get(`/clients/${clientId}/contacts`),
  addContact: (clientId: string, data: unknown) =>
    axiosInstance.post(`/clients/${clientId}/contacts`, data),
  updateContact: (clientId: string, contactId: string, data: unknown) =>
    axiosInstance.put(`/clients/${clientId}/contacts/${contactId}`, data),
  deleteContact: (clientId: string, contactId: string) =>
    axiosInstance.delete(`/clients/${clientId}/contacts/${contactId}`),
  // Modules sub-resource
  getModules: (clientId: string) =>
    axiosInstance.get(`/clients/${clientId}/modules`),
  activateModule: (clientId: string, moduleType: string, config?: Record<string, unknown>) =>
    axiosInstance.post(`/clients/${clientId}/modules`, { moduleType, config }),
  deactivateModule: (clientId: string, moduleType: string) =>
    axiosInstance.delete(`/clients/${clientId}/modules/${moduleType}`),
  // Credentials sub-resource (solo Super Admin)
  getCredentials: (clientId: string) =>
    axiosInstance.get(`/clients/${clientId}/credentials`),
  getCredentialPassword: (clientId: string, credentialId: string) =>
    axiosInstance.get(`/clients/${clientId}/credentials/${credentialId}/password`),
  createCredential: (clientId: string, data: unknown) =>
    axiosInstance.post(`/clients/${clientId}/credentials`, data),
  updateCredential: (clientId: string, credentialId: string, data: unknown) =>
    axiosInstance.put(`/clients/${clientId}/credentials/${credentialId}`, data),
  deleteCredential: (clientId: string, credentialId: string) =>
    axiosInstance.delete(`/clients/${clientId}/credentials/${credentialId}`),
}

export const projectsApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/projects', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/projects/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/projects', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/projects/${id}`, data),
  updateStatus: (id: string, status: string) =>
    axiosInstance.patch(`/projects/${id}/status`, { status }),
  getTeam: (id: string) =>
    axiosInstance.get(`/projects/${id}/team`),
  addTeamMember: (id: string, data: { userId: string; role: string }) =>
    axiosInstance.post(`/projects/${id}/team`, data),
  removeTeamMember: (id: string, userId: string) =>
    axiosInstance.delete(`/projects/${id}/team/${userId}`),
  getBudget: (id: string) =>
    axiosInstance.get(`/projects/${id}/budget`),
  // Control Room (progetti evento)
  getControlRoom: (id: string) =>
    axiosInstance.get(`/projects/${id}/control-room`),
  updateEventStatus: (id: string, status: string) =>
    axiosInstance.patch(`/projects/${id}/event-status`, { status }),
  updateChecklist: (id: string, checklist: unknown[]) =>
    axiosInstance.put(`/projects/${id}/checklist`, { checklist }),
  updateEventTeam: (id: string, teamMembers: unknown[]) =>
    axiosInstance.put(`/projects/${id}/event-team`, { teamMembers }),
  addEventUpdate: (id: string, data: { message: string; type: string }) =>
    axiosInstance.post(`/projects/${id}/event-updates`, data),
  // Note
  getNotes: (id: string) =>
    axiosInstance.get(`/projects/${id}/notes`),
  addNote: (id: string, text: string) =>
    axiosInstance.post(`/projects/${id}/notes`, { text }),
  deleteNote: (id: string, noteId: string) =>
    axiosInstance.delete(`/projects/${id}/notes/${noteId}`),
  // Commenti/Aggiornamenti
  getComments: (id: string) =>
    axiosInstance.get(`/projects/${id}/comments`),
  addComment: (id: string, text: string, mentions: string[]) =>
    axiosInstance.post(`/projects/${id}/comments`, { text, mentions }),
  deleteComment: (id: string, commentId: string) =>
    axiosInstance.delete(`/projects/${id}/comments/${commentId}`),
  // Documenti progetto (multi-file)
  listDocuments: (id: string) =>
    axiosInstance.get(`/projects/${id}/documents`),
  uploadDocument: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return axiosInstance.post(`/projects/${id}/documents`, fd, { baseURL: UPLOAD_BASE_URL, headers: { 'Content-Type': 'multipart/form-data' } })
  },
  deleteDocument: (projectId: string, docId: string) =>
    axiosInstance.delete(`/projects/${projectId}/documents/${docId}`),
}

export const tasksApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/tasks', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/tasks/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/tasks', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/tasks/${id}`, data),
  updateStatus: (id: string, status: string) =>
    axiosInstance.patch(`/tasks/${id}/status`, { status }),
  getMyTasks: () =>
    axiosInstance.get('/tasks/my'),
}

export const dashboardApi = {
  get: () => apiClient.get<any>('/dashboard'),
  admin: () => apiClient.get<any>('/dashboard/admin'),
  manager: () => apiClient.get<any>('/dashboard/manager'),
  operative: () => apiClient.get<any>('/dashboard/operative'),
  today: () => apiClient.get<any>('/dashboard/today'),
}

export const notificationsApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/notifications', { params }),
  markAsRead: (data: { ids?: string[]; all?: boolean }) =>
    axiosInstance.post('/notifications/mark-read', data),
  getUnreadCount: () =>
    axiosInstance.get<{ data: { count: number } }>('/notifications/unread-count'),
}

export const calendarApi = {
  getEvents: (params: Record<string, unknown>) =>
    axiosInstance.get('/calendar', { params }),
}

export const searchApi = {
  globalSearch: (params: Record<string, unknown>) =>
    axiosInstance.get('/search', { params }),
}

export const aiApi = {
  chat: (data: { message: string; conversationId?: string; provider?: string; context?: string }) =>
    axiosInstance.post('/ai/chat', data),
  suggest: (data: { type: string; context: string; platform?: string; tone?: string }) =>
    axiosInstance.post('/ai/suggest', data),
  generate: (data: { type: string; brief: string; clientContext?: string }) =>
    axiosInstance.post('/ai/generate', data),
}

export const filesApi = {
  upload: (formData: FormData) =>
    axiosInstance.post('/files/upload', formData, { baseURL: UPLOAD_BASE_URL, headers: { 'Content-Type': 'multipart/form-data' } }),
  getFile: (id: string) =>
    axiosInstance.get(`/files/${id}`),
  deleteFile: (id: string) =>
    axiosInstance.delete(`/files/${id}`),
}

export const usersApi = {
  list: (params?: Record<string, unknown>) => apiClient.get<any>('/users', params),
  getById: (id: string) => apiClient.get<any>(`/users/${id}`),
  create: (data: Record<string, unknown>) => apiClient.post<any>('/users', data),
  update: (id: string, data: Record<string, unknown>) => apiClient.patch<any>(`/users/${id}`, data),
  remove: (id: string) => apiClient.delete<any>(`/users/${id}`),
  hardDelete: (id: string, replacementId?: string) =>
    axiosInstance.delete(`/users/${id}/permanent`, { data: replacementId ? { replacementId } : undefined }),
  getTeamWorkload: () => apiClient.get<any>('/users/team-workload'),
  getOnlineUsers: () => apiClient.get<string[]>('/users/online'),
  getOrgChart: () => apiClient.get<any>('/users/org-chart'),
  getPassword: (id: string) => apiClient.get<{ password: string | null }>(`/users/${id}/password`),
}

export const departmentsApi = {
  list: () => apiClient.get<any>('/departments'),
}

export const rolesApi = {
  list: (params?: Record<string, unknown>) => apiClient.get<any>('/roles', params),
}

export const reportsApi = {
  dashboard: (params?: Record<string, unknown>) => apiClient.get<any>('/dashboard', params),
  get: (params: Record<string, unknown>) => apiClient.get<any>('/reports', params),
  globalStats: () => apiClient.get<any>('/reports/global-stats'),
}

export const auditLogApi = {
  list: (params?: Record<string, unknown>) => apiClient.get<any>('/audit-log', params),
  recent: (limit?: number) => apiClient.get<any>('/audit-log/recent', { limit: limit ?? 20 }),
  getByEntity: (entity: string, entityId: string) => apiClient.get<any>(`/audit-log/entity/${entity}/${entityId}`),
}

export const aiHealthApi = {
  getStats: (days?: number) => apiClient.get<any>('/ai-health/health-stats', days ? { days } : undefined),
}

export const telegramApi = {
  getLinkCode: () => apiClient.post<{ code: string; deepLink: string; expiresAt: string }>('/bot/telegram/link-code'),
  getMappings: () => apiClient.get<Array<{ platform: string; platformUserId: string; verifiedAt: string | null }>>('/bot/mappings'),
  unlink: () => apiClient.delete<{ success: boolean }>('/bot/telegram/link'),
}

export const pedApi = {
  listPlans: (params?: Record<string, unknown>) => apiClient.get<any>('/ped/plans', params),
  getPlan: (id: string) => apiClient.get<any>(`/ped/plans/${id}`),
  createPlan: (data: unknown) => apiClient.post<any>('/ped/plans', data),
  updatePlan: (id: string, data: unknown) => apiClient.put<any>(`/ped/plans/${id}`, data),
  deletePlan: (id: string) => apiClient.delete<any>(`/ped/plans/${id}`),
  getPlanContents: (planId: string) => apiClient.get<any>(`/ped/plans/${planId}/contents`),
  getProjectContents: (projectId: string) => apiClient.get<any>(`/ped/projects/${projectId}/contents`),
  getGuestPlan: () => apiClient.get<any>('/ped/guest/plan'),
  createContent: (planId: string, data: unknown) => apiClient.post<any>(`/ped/plans/${planId}/contents`, data),
  createContentForProject: (projectId: string, data: unknown) => apiClient.post<any>(`/ped/projects/${projectId}/contents`, data),
  updateContent: (id: string, data: unknown) => apiClient.put<any>(`/ped/contents/${id}`, data),
  updateContentCaption: (id: string, data: unknown) => apiClient.patch<any>(`/ped/contents/${id}/caption`, data),
  deleteContent: (id: string) => apiClient.delete<any>(`/ped/contents/${id}`),
  getContentComments: (id: string) => apiClient.get<any>(`/ped/contents/${id}/comments`),
  addContentComment: (id: string, text: string, type?: string) => apiClient.post<any>(`/ped/contents/${id}/comments`, { text, type }),
  getContentVersions: (id: string) => apiClient.get<any>(`/ped/contents/${id}/versions`),
  // Content Assets
  uploadAsset: (contentId: string, file: File, onUploadProgress?: (e: { loaded: number; total?: number }) => void) => {
    const fd = new FormData()
    fd.append('file', file)
    return axiosInstance.post(`/ped/contents/${contentId}/assets`, fd, {
      baseURL: UPLOAD_BASE_URL,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...(onUploadProgress ? { onUploadProgress } : {}),
    })
  },
  deleteAsset: (assetId: string) =>
    axiosInstance.delete(`/ped/assets/${assetId}`),
  getAssetUrl: (assetId: string) =>
    axiosInstance.get(`/ped/assets/${assetId}/url`),
  streamAssetUrl: (assetId: string) =>
    axiosInstance.get(`/ped/assets/${assetId}/stream`, { responseType: 'blob' }),
  // Share
  getShareLink: (planId: string) => apiClient.get<any>(`/ped/plans/${planId}/share`),
  generateShareLink: (planId: string) => apiClient.post<any>(`/ped/plans/${planId}/share`, {}),
  revokeShareLink: (planId: string) => apiClient.delete<any>(`/ped/plans/${planId}/share`),
  // Export PDF
  exportPdf: (projectId: string, params?: { year?: number; month?: number }) =>
    axiosInstance.get(`/ped/projects/${projectId}/export-pdf`, {
      params,
      responseType: 'blob',
    }),
}

export const advApi = {
  listCampaigns: (params?: Record<string, unknown>) => apiClient.get<any>('/adv/campaigns', params),
  getCampaign: (id: string) => apiClient.get<any>(`/adv/campaigns/${id}`),
  createCampaign: (data: unknown) => apiClient.post<any>('/adv/campaigns', data),
  updateCampaign: (id: string, data: unknown) => apiClient.put<any>(`/adv/campaigns/${id}`, data),
  deleteCampaign: (id: string) => apiClient.delete<any>(`/adv/campaigns/${id}`),
}

export const smmApi = {
  // Impostazioni e saldo del panel
  getSettings: () => apiClient.get<any>('/smm/settings'),
  updateSettings: (data: unknown) => apiClient.put<any>('/smm/settings', data),
  getBalance: (panelId?: string) => apiClient.get<any>('/smm/balance', panelId ? { panelId } : undefined),
  listPanels: () => apiClient.get<any>('/smm/panels'),
  createPanel: (data: { name: string; apiUrl: string; apiKey: string }) => apiClient.post<any>('/smm/panels', data),
  updatePanel: (id: string, data: { name?: string; apiUrl?: string; apiKey?: string; enabled?: boolean }) =>
    apiClient.patch<any>(`/smm/panels/${id}`, data),

  // Catalogo e listino curato
  listServices: (params?: Record<string, unknown>) => apiClient.get<any>('/smm/services', params),
  listTipi: () => apiClient.get<any>('/smm/services/tipi'),
  listCategories: (params?: Record<string, unknown>) => apiClient.get<any>('/smm/services/categories', params),
  syncCatalog: (panelId?: string) => apiClient.post<any>('/smm/services/sync', panelId ? { panelId } : {}),
  updateService: (id: string, data: unknown) => apiClient.patch<any>(`/smm/services/${id}`, data),

  // Ordini — quoteOrder NON spende nulla, createOrder SÌ
  quoteOrder: (data: unknown) => apiClient.post<any>('/smm/orders/quote', data),
  createOrder: (data: unknown) => apiClient.post<any>('/smm/orders', data),
  listOrders: (params?: Record<string, unknown>) => apiClient.get<any>('/smm/orders', params),
  getOrder: (id: string) => apiClient.get<any>(`/smm/orders/${id}`),
  updateOrder: (id: string, data: unknown) => apiClient.patch<any>(`/smm/orders/${id}`, data),
  syncOrders: (orderIds?: string[]) => apiClient.post<any>('/smm/orders/sync', { orderIds }),
  cancelOrder: (id: string) => apiClient.post<any>(`/smm/orders/${id}/cancel`, {}),
  refillOrder: (id: string) => apiClient.post<any>(`/smm/orders/${id}/refill`, {}),

  // Panoramica: ultimi acquisti, più usati, consegne
  getDashboard: (params?: Record<string, unknown>) => apiClient.get<any>('/smm/dashboard', params),
  // Crescita arrivata per profilo social, e profilo scritto a mano su un ordine
  getProfili: (params?: Record<string, unknown>) => apiClient.get<any>('/smm/profiles', params),
  setOrderProfile: (id: string, profileHandle: string | null) =>
    apiClient.put<any>(`/smm/orders/${id}/profile`, { profileHandle }),

  // Statistiche: spesa, ricavo, margine
  getStats: (params?: Record<string, unknown>) => apiClient.get<any>('/smm/stats', params),

  // Negozio Commenti — link pubblici per i clienti, vendita di commenti IG/FB.
  // La pagina del cliente (/shop/commenti/:token) non passa da qui: usa fetch
  // diretto, perché questi endpoint sono autenticati (token utente, non token link).
  listShopLinks: () => apiClient.get<any>('/smm/shop/links'),
  getShopLink: (id: string) => apiClient.get<any>(`/smm/shop/links/${id}`),
  createShopLink: (data: unknown) => apiClient.post<any>('/smm/shop/links', data),
  updateShopLink: (id: string, data: unknown) => apiClient.patch<any>(`/smm/shop/links/${id}`, data),
  regenerateShopLinkToken: (id: string) => apiClient.post<any>(`/smm/shop/links/${id}/token`, {}),
  addShopLinkMovimento: (id: string, data: { importoEur: number; nota?: string }) =>
    apiClient.post<any>(`/smm/shop/links/${id}/movimenti`, data),

  // Tipologie di commento — cosa il cliente sceglie dopo la piattaforma (Standard, Italiani, Premium…)
  listShopTipi: () => apiClient.get<any>('/smm/shop/tipi'),
  // cosa: COMMENTI (commenti personalizzati) | LIKE (mi piace)
  getShopServiziCommenti: (platform: string, cosa: string = 'COMMENTI') =>
    apiClient.get<any>('/smm/shop/servizi-commenti', { platform, cosa }),
  createShopTipo: (data: unknown) => apiClient.post<any>('/smm/shop/tipi', data),
  updateShopTipo: (id: string, data: unknown) => apiClient.patch<any>(`/smm/shop/tipi/${id}`, data),
  deleteShopTipo: (id: string) => apiClient.delete<any>(`/smm/shop/tipi/${id}`),
}

export const consulenzaApi = {
  // Progetti consulenza
  listProjects: (params?: Record<string, unknown>) => apiClient.get<any>('/consulenza/projects', params),
  getProject: (id: string) => apiClient.get<any>(`/consulenza/projects/${id}`),
  createProject: (data: unknown) => apiClient.post<any>('/consulenza/projects', data),
  updateProject: (id: string, data: unknown) => apiClient.put<any>(`/consulenza/projects/${id}`, data),
  deleteProject: (id: string) => apiClient.delete<any>(`/consulenza/projects/${id}`),
  // Sessioni
  listSessions: (projectId: string, params?: Record<string, unknown>) =>
    apiClient.get<any>(`/consulenza/projects/${projectId}/sessions`, params),
  createSession: (projectId: string, data: unknown) =>
    apiClient.post<any>(`/consulenza/projects/${projectId}/sessions`, data),
  updateSession: (id: string, data: unknown) => apiClient.put<any>(`/consulenza/sessions/${id}`, data),
  deleteSession: (id: string) => apiClient.delete<any>(`/consulenza/sessions/${id}`),
}

export const ticketsApi = {
  list: (params?: Record<string, unknown>) => apiClient.get<any>('/digital/tickets', params),
  getById: (id: string) => apiClient.get<any>(`/digital/tickets/${id}`),
  create: (data: unknown) => apiClient.post<any>('/digital/tickets', data),
  update: (id: string, data: unknown) => apiClient.put<any>(`/digital/tickets/${id}`, data),
  delete: (id: string) => apiClient.delete<any>(`/digital/tickets/${id}`),
  updateStatus: (id: string, status: string) =>
    axiosInstance.patch(`/digital/tickets/${id}/status`, { status }),
  getClientOverview: (clientId: string) =>
    apiClient.get<any>(`/digital/clients/${clientId}/overview`),
}

// Alias semantico per il modulo Digital
export const digitalApi = ticketsApi

export const prApi = {
  // Comunicati stampa
  listPressReleases: (params?: Record<string, unknown>) =>
    apiClient.get<any>('/pr/press-releases', params),
  getPressRelease: (id: string) => apiClient.get<any>(`/pr/press-releases/${id}`),
  createPressRelease: (data: unknown) => apiClient.post<any>('/pr/press-releases', data),
  updatePressRelease: (id: string, data: unknown) =>
    apiClient.put<any>(`/pr/press-releases/${id}`, data),
  deletePressRelease: (id: string) => apiClient.delete<any>(`/pr/press-releases/${id}`),
  // Contatti media
  listMediaContacts: (params?: Record<string, unknown>) =>
    apiClient.get<any>('/pr/media-contacts', params),
  createMediaContact: (data: unknown) => apiClient.post<any>('/pr/media-contacts', data),
  updateMediaContact: (id: string, data: unknown) =>
    apiClient.put<any>(`/pr/media-contacts/${id}`, data),
  deleteMediaContact: (id: string) => apiClient.delete<any>(`/pr/media-contacts/${id}`),
  // Rassegna stampa
  listClippings: (params?: Record<string, unknown>) =>
    apiClient.get<any>('/pr/clippings', params),
  createClipping: (data: unknown) => apiClient.post<any>('/pr/clippings', data),
  updateClipping: (id: string, data: unknown) => apiClient.put<any>(`/pr/clippings/${id}`, data),
  deleteClipping: (id: string) => apiClient.delete<any>(`/pr/clippings/${id}`),
}

export const brandingApi = {
  listProjects: (params?: Record<string, unknown>) => apiClient.get<any>('/branding/projects', params),
  getProject: (id: string) => apiClient.get<any>(`/branding/projects/${id}`),
  createProject: (data: unknown) => apiClient.post<any>('/branding/projects', data),
  updateProject: (id: string, data: unknown) => apiClient.put<any>(`/branding/projects/${id}`, data),
  deleteProject: (id: string) => apiClient.delete<any>(`/branding/projects/${id}`),
  listDeliverables: (projectId: string) => apiClient.get<any>(`/branding/projects/${projectId}/deliverables`),
  createDeliverable: (projectId: string, data: unknown) => apiClient.post<any>(`/branding/projects/${projectId}/deliverables`, data),
  updateDeliverable: (id: string, data: unknown) => apiClient.put<any>(`/branding/deliverables/${id}`, data),
  deleteDeliverable: (id: string) => apiClient.delete<any>(`/branding/deliverables/${id}`),
}

export const accountingApi = {
  summary: (params?: Record<string, unknown>) =>
    axiosInstance.get('/accounting/summary', { params }),
  markQuotePaid: (quoteId: string, amount?: number) =>
    axiosInstance.patch(`/accounting/quotes/${quoteId}/mark-paid`, amount !== undefined ? { amount } : {}),
  unmarkQuotePaid: (quoteId: string) =>
    axiosInstance.patch(`/accounting/quotes/${quoteId}/unmark-paid`),
}

export const bankImportApi = {
  upload: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return axiosInstance.post('/bank-import/upload', fd, { baseURL: UPLOAD_BASE_URL, headers: { 'Content-Type': 'multipart/form-data' } })
  },
  listTransactions: (params?: Record<string, unknown>) =>
    axiosInstance.get('/bank-import/transactions', { params }),
  exportAll: () =>
    axiosInstance.get('/bank-import/transactions/export', { responseType: 'blob' }),
  getStats: (params?: Record<string, string>) =>
    axiosInstance.get('/bank-import/stats', { params }),
  categorize: (id: string, data: { category: string; projectId?: string; clientId?: string }) =>
    axiosInstance.patch(`/bank-import/transactions/${id}/categorize`, data),
  dismiss: (id: string) =>
    axiosInstance.patch(`/bank-import/transactions/${id}/dismiss`),
  confirmIncome: (id: string, data: { quoteId?: string; clientId?: string }) =>
    axiosInstance.patch(`/bank-import/transactions/${id}/confirm-income`, data),
  reprocess: () =>
    axiosInstance.post('/bank-import/reprocess'),
  linkToProject: (id: string, data: { projectId: string | null; clientId?: string | null }) =>
    axiosInstance.patch(`/bank-import/transactions/${id}/link`, data),
  resetTransaction: (id: string) =>
    axiosInstance.patch(`/bank-import/transactions/${id}/reset`),
  resetByExpense: (expenseId: string) =>
    axiosInstance.patch(`/bank-import/expense/${expenseId}/reset`),
  linkToInvoice: (id: string, ficInvoiceId: string) =>
    axiosInstance.patch(`/bank-import/transactions/${id}/link-invoice`, { ficInvoiceId }),
  allocate: (transactionId: string, allocations: Array<{ invoiceId: string; amount: number }>) =>
    axiosInstance.post(`/bank-import/transactions/${transactionId}/allocate`, { allocations }),
}

export const salaryApi = {
  list: () => axiosInstance.get('/salaries'),
  stats: () => axiosInstance.get('/salaries/stats'),
  create: (data: Record<string, unknown>) => axiosInstance.post('/salaries', data),
  update: (id: string, data: Record<string, unknown>) => axiosInstance.patch(`/salaries/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/salaries/${id}`),
  adjustExpense: (expenseId: string, data: { amount: number; notes?: string | undefined }) =>
    axiosInstance.patch(`/salaries/expenses/${expenseId}`, data),
  // Cedolini (buste paga) per dipendente
  listPayslips: (salaryId: string) => apiClient.get<any>(`/salaries/${salaryId}/payslips`),
  payslipUrl: (payslipId: string) => apiClient.get<any>(`/salaries/payslips/${payslipId}/url`),
  updatePayslip: (payslipId: string, data: { netAmount: number | null }) => apiClient.patch<any>(`/salaries/payslips/${payslipId}`, data),
  importPayslips: (form: FormData) =>
    axiosInstance.post('/salaries/payslips/import', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 }),
}

export const ficApi = {
  status: () =>
    axiosInstance.get('/fic/status'),
  getAuthUrl: () =>
    axiosInstance.get('/fic/auth-url'),
  sync: () =>
    axiosInstance.post('/fic/sync'),
  invoices: (params?: Record<string, unknown>) =>
    axiosInstance.get('/fic/invoices', { params }),
  matchInvoice: (id: string, data: { clientId?: string; projectId?: string; quoteId?: string }) =>
    axiosInstance.patch(`/fic/invoices/${id}/match`, data),
  reconciliation: (params?: Record<string, unknown>) =>
    axiosInstance.get('/fic/reconciliation', { params }),
  disconnect: () =>
    axiosInstance.post('/fic/disconnect'),
  invoiceLines: (id: string) =>
    axiosInstance.get(`/fic/invoices/${id}/lines`),
  setSplits: (id: string, splits: Array<{ projectId: string; amount: number; description: string }>) =>
    axiosInstance.put(`/fic/invoices/${id}/splits`, { splits }),
  markPaid: (id: string, data: { amount: number; date: string; method: 'cash' | 'other' }) =>
    axiosInstance.post(`/fic/invoices/${id}/mark-paid`, data),
  reconcilePreview: () =>
    axiosInstance.post('/fic/reconcile', { dryRun: true }),
  reconcileWithAi: () =>
    // effort "high" lascia Claude ragionare sui casi ambigui: può richiedere
    // oltre 30s (default) su un lotto grande di movimenti non collegati.
    axiosInstance.post('/fic/reconcile/ai', {}, { timeout: 180_000 }),
}

export const expensesApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/expenses', { params }),
  summary: (params?: Record<string, unknown>) =>
    axiosInstance.get('/expenses/summary', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/expenses/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/expenses', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/expenses/${id}`, data),
  delete: (id: string) =>
    axiosInstance.delete(`/expenses/${id}`),
  uploadReceipt: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('receipt', file)
    return axiosInstance.post(`/expenses/${id}/receipt`, fd, {
      baseURL: UPLOAD_BASE_URL,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  deleteReceipt: (id: string) =>
    axiosInstance.delete(`/expenses/${id}/receipt`),
  getReceiptUrl: (id: string) =>
    axiosInstance.get(`/expenses/${id}/receipt`),
}

export const quotesApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/quotes', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/quotes/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/quotes', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/quotes/${id}`, data),
  updateStatus: (id: string, status: string) =>
    axiosInstance.patch(`/quotes/${id}/status`, { status }),
  delete: (id: string) =>
    axiosInstance.delete(`/quotes/${id}`),
  createProjectFromLineItem: (lineItemId: string, data: unknown) =>
    axiosInstance.post(`/quotes/line-items/${lineItemId}/create-project`, data),
  linkLineItemToProject: (lineItemId: string, projectId: string | null) =>
    axiosInstance.patch(`/quotes/line-items/${lineItemId}/link-project`, { projectId }),
  downloadPdf: (id: string) =>
    axiosInstance.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  createRevision: (id: string) =>
    axiosInstance.post(`/quotes/${id}/revision`),
  createProjectFromQuote: (id: string, data: unknown) =>
    axiosInstance.post(`/quotes/${id}/create-project`, data),
  getRevisionChain: (id: string) =>
    axiosInstance.get(`/quotes/${id}/revisions`),
  getByProject: (projectId: string) =>
    axiosInstance.get(`/quotes/by-project/${projectId}`),
  uploadProjectPdf: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return axiosInstance.post(`/quotes/${id}/project-pdf`, fd, {
      baseURL: UPLOAD_BASE_URL,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    })
  },
  deleteProjectPdf: (id: string) =>
    axiosInstance.delete(`/quotes/${id}/project-pdf`),
  analyzeProjectPdf: async (file: File): Promise<{ // eslint-disable-line
    title: string
    notes: string
    clientName: string | null
    hasExistingQuote: boolean
    lineItems: Array<{ description: string; moduleType: string; quantity: number; unitPrice: number; unit: string }>
    cleanPdfBase64: string
    originalFileName: string
  }> => {
    const fd = new FormData()
    fd.append('file', file)
    const resp = await axiosInstance.post('/quotes/analyze-project-pdf', fd, {
      baseURL: UPLOAD_BASE_URL,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })
    const raw = resp.data as any
    return raw?.data ?? raw
  },
}

export const suppliersApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/suppliers', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/suppliers/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/suppliers', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/suppliers/${id}`, data),
  remove: (id: string) =>
    axiosInstance.delete(`/suppliers/${id}`),
}

export const catalogApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/catalog', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/catalog/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/catalog', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/catalog/${id}`, data),
  remove: (id: string) =>
    axiosInstance.delete(`/catalog/${id}`),
  reorder: (items: { id: string; sortOrder: number }[]) =>
    axiosInstance.patch('/catalog/reorder', { items }),
}

export const calendarEventsApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/calendar/events', { params }),
  create: (data: unknown) =>
    axiosInstance.post('/calendar/events', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/calendar/events/${id}`, data),
  delete: (id: string) =>
    axiosInstance.delete(`/calendar/events/${id}`),
  respond: (id: string, status: string) =>
    axiosInstance.patch(`/calendar/events/${id}/respond`, { status }),
  respondInvitee: (id: string, status: string, note?: string) =>
    axiosInstance.patch(`/calendar/events/${id}/respond-invitee`, { status, note }),
}

export const ragApi = {
  upload: (formData: FormData) =>
    axiosInstance.post('/rag/upload', formData, {
      baseURL: UPLOAD_BASE_URL,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    }),
  listDocuments: (params?: Record<string, unknown>) =>
    axiosInstance.get('/rag/documents', { params }),
  getDocument: (id: string) =>
    axiosInstance.get(`/rag/documents/${id}`),
  deleteDocument: (id: string) =>
    axiosInstance.delete(`/rag/documents/${id}`),
  search: (query: string, topK?: number) =>
    axiosInstance.post('/rag/search', { query, topK }),
  getStatus: () =>
    axiosInstance.get('/rag/status'),
}

export const emailApi = {
  // Account
  getAccounts: () =>
    axiosInstance.get('/email/accounts'),
  createAccount: (data: unknown) =>
    axiosInstance.post('/email/accounts', data),
  deleteAccount: (id: string) =>
    axiosInstance.delete(`/email/accounts/${id}`),
  updateAccountPassword: (id: string, password: string) =>
    axiosInstance.put(`/email/accounts/${id}/password`, { password }),
  testAccount: (id: string) =>
    axiosInstance.post(`/email/accounts/${id}/test`),
  syncAccount: (id: string) =>
    axiosInstance.post(`/email/accounts/${id}/sync`, {}, { timeout: 120_000 }),
  // Messages
  getMessages: (params?: Record<string, unknown>) =>
    axiosInstance.get('/email/messages', { params }),
  getMessage: (id: string) =>
    axiosInstance.get(`/email/messages/${id}`),
  getMessageBody: (id: string) =>
    axiosInstance.get(`/email/messages/${id}/body`),
  getThread: (id: string) =>
    axiosInstance.get(`/email/messages/${id}/thread`),
  // Actions
  send: (data: FormData | Record<string, unknown>) =>
    axiosInstance.post('/email/send', data, {
      timeout: 120_000,
      ...(data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
    }),
  updateSignature: (id: string, signature: string | null) =>
    axiosInstance.put(`/email/accounts/${id}/signature`, { signature }),
  markRead: (id: string, isRead: boolean) =>
    axiosInstance.patch(`/email/messages/${id}/read`, { isRead }),
  toggleStar: (id: string) =>
    axiosInstance.patch(`/email/messages/${id}/star`),
  linkMessage: (id: string, data: { clientId?: string; projectId?: string }) =>
    axiosInstance.patch(`/email/messages/${id}/link`, data),
  getUnreadCount: (params?: Record<string, unknown>) =>
    axiosInstance.get('/email/unread-count', { params }),
  downloadAttachment: (attachmentId: string) =>
    axiosInstance.get(`/email/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  // Drafts
  saveDraft: (data: Record<string, unknown>) =>
    axiosInstance.post('/email/drafts', data),
  updateDraft: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/email/drafts/${id}`, data),
  deleteDraft: (id: string) =>
    axiosInstance.delete(`/email/drafts/${id}`),
  sendDraft: (id: string, data?: FormData) =>
    axiosInstance.post(`/email/drafts/${id}/send`, data ?? {}, {
      timeout: 120_000,
      ...(data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
    }),
  // Delete
  deleteMessages: (ids: string[]) =>
    axiosInstance.delete('/email/messages', { data: { ids } }),
  // Contacts autocomplete
  searchContacts: (q: string) =>
    axiosInstance.get('/email/contacts', { params: { q } }),
  // Labels (cartelle virtuali)
  listLabels: () =>
    axiosInstance.get('/email/labels'),
  createLabel: (name: string, color?: string) =>
    axiosInstance.post('/email/labels', { name, color }),
  deleteLabel: (id: string) =>
    axiosInstance.delete(`/email/labels/${id}`),
  assignLabel: (messageId: string, label: string | null) =>
    axiosInstance.patch(`/email/messages/${messageId}/label`, { label }),
  // OAuth
  getGoogleAuthUrl: () =>
    axiosInstance.get('/email/oauth/google/url'),
  getMicrosoftAuthUrl: () =>
    axiosInstance.get('/email/oauth/microsoft/url'),
  // AI
  aiGenerateReply: (threadHtml: string, senderName?: string, subject?: string, myName?: string) =>
    axiosInstance.post('/email/ai/generate-reply', { threadHtml, senderName, subject, myName }, { timeout: 60_000 }),
  aiPolish: (text: string, tone?: 'formal' | 'friendly' | 'normal') =>
    axiosInstance.post('/email/ai/polish', { text, tone }, { timeout: 60_000 }),
  aiTranslate: (html: string, targetLang: 'it' | 'en') =>
    axiosInstance.post('/email/ai/translate', { html, targetLang }, { timeout: 60_000 }),
}

export const resourcesApi = {
  // Asset Informativi
  listInformativi: (projectId?: string) =>
    axiosInstance.get('/risorse/informativi', { params: projectId ? { projectId } : {} }),
  getInformativo: (id: string) =>
    axiosInstance.get(`/risorse/informativi/${id}`),
  createInformativo: (data: unknown) =>
    axiosInstance.post('/risorse/informativi', data),
  updateInformativo: (id: string, data: unknown) =>
    axiosInstance.put(`/risorse/informativi/${id}`, data),
  deleteInformativo: (id: string) =>
    axiosInstance.delete(`/risorse/informativi/${id}`),
  uploadInformativoFile: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return axiosInstance.post(`/risorse/informativi/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  downloadInformativoFile: (id: string) =>
    axiosInstance.get(`/risorse/informativi/${id}/download`, { responseType: 'blob' }),
  // Asset Operativi
  listOperativi: (projectId?: string) =>
    axiosInstance.get('/risorse/operativi', { params: projectId ? { projectId } : {} }),
  getOperativo: (id: string, reveal = false) =>
    axiosInstance.get(`/risorse/operativi/${id}`, { params: reveal ? { reveal: 'true' } : {} }),
  createOperativo: (data: unknown) =>
    axiosInstance.post('/risorse/operativi', data),
  updateOperativo: (id: string, data: unknown) =>
    axiosInstance.put(`/risorse/operativi/${id}`, data),
  deleteOperativo: (id: string) =>
    axiosInstance.delete(`/risorse/operativi/${id}`),
  // Codici
  listCodici: (projectId?: string) =>
    axiosInstance.get('/risorse/codici', { params: projectId ? { projectId } : {} }),
  getCodice: (id: string) =>
    axiosInstance.get(`/risorse/codici/${id}`),
  createCodice: (data: unknown) =>
    axiosInstance.post('/risorse/codici', data),
  updateCodice: (id: string, data: unknown) =>
    axiosInstance.put(`/risorse/codici/${id}`, data),
  deleteCodice: (id: string) =>
    axiosInstance.delete(`/risorse/codici/${id}`),
}

export const subscriptionsApi = {
  list: () =>
    axiosInstance.get('/risorse/abbonamenti'),
  getById: (id: string, reveal = false) =>
    axiosInstance.get(`/risorse/abbonamenti/${id}`, { params: reveal ? { reveal: 'true' } : {} }),
  create: (data: unknown) =>
    axiosInstance.post('/risorse/abbonamenti', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/risorse/abbonamenti/${id}`, data),
  delete: (id: string) =>
    axiosInstance.delete(`/risorse/abbonamenti/${id}`),
  getUsage: (id: string) =>
    axiosInstance.get(`/risorse/abbonamenti/${id}/usage`),
  toggleActive: (id: string) =>
    axiosInstance.patch(`/risorse/abbonamenti/${id}/toggle-active`),
}

export const socialApi = {
  // Articles (from n8n)
  listArticles: () => axiosInstance.get('/social/articles'),
  approveArticle: (executionId: string, editedData?: { title?: string; summary?: string; content?: string; hashtags?: string[]; caption?: string }, scheduledAt?: string, publishTo?: 'all' | 'site' | 'social') =>
    axiosInstance.post<{
      success: boolean;
      data?: {
        site: boolean;
        social: { facebook: boolean; instagram: boolean; linkedin: boolean };
        errors: string[];
      };
      message?: string;
    }>('/social/articles/approve', { executionId, editedData, scheduledAt, publishTo: publishTo || 'all' }, { timeout: 120_000 }),
  generateCaption: (articleId: string) =>
    axiosInstance.post<{ success: boolean; data: { caption: string } }>('/social/articles/generate-caption', { articleId }, { timeout: 30_000 }),
  rejectArticle: (executionId: string) => axiosInstance.post('/social/articles/reject', { executionId }),
  bulkApprove: (executionIds: string[]) => axiosInstance.post('/social/articles/bulk-approve', { executionIds }),
  bulkReject: (executionIds: string[]) => axiosInstance.post('/social/articles/bulk-reject', { executionIds }),
  generateImage: (articleId: string, prompt: string, title?: string) =>
    axiosInstance.post<{ success: boolean; data: { imageUrl: string } }>('/social/articles/generate-image', { articleId, prompt, title }, { timeout: 120_000 }),
  publishRemaining: (articleId: string, target: 'site' | 'social') =>
    axiosInstance.post<{
      success: boolean;
      data?: {
        site?: boolean;
        social?: { facebook: boolean; instagram: boolean; linkedin: boolean };
        errors: string[];
      };
    }>('/social/articles/publish-remaining', { articleId, target }, { timeout: 120_000 }),
  getPublishStatus: () => axiosInstance.get('/social/articles/publish-status'),
  createFromTopic: (topic: string) =>
    axiosInstance.post('/social/articles/create-from-topic', { topic }, { timeout: 120_000 }),
  publishCustom: (article: any, publishTo?: 'all' | 'site' | 'social') =>
    axiosInstance.post('/social/articles/publish-custom', { article, publishTo: publishTo || 'all' }),
  getStats: () => axiosInstance.get('/social/stats'),
  triggerWorkflow: () => axiosInstance.post('/social/trigger', {}, { timeout: 300_000 }),
  // Platform config
  listConfigs: () => axiosInstance.get('/social/config'),
  getConfig: (platform: string, reveal = false) =>
    axiosInstance.get(`/social/config/${platform}`, { params: reveal ? { reveal: 'true' } : {} }),
  upsertConfig: (platform: string, data: { label: string; config: Record<string, string> }) =>
    axiosInstance.put(`/social/config/${platform}`, data),
  deleteConfig: (platform: string) => axiosInstance.delete(`/social/config/${platform}`),
  // Formats
  listFormats: (projectId?: string) => axiosInstance.get('/social/formats', { params: { projectId } }),
  getFormat: (id: string) => axiosInstance.get(`/social/formats/${id}`),
  createFormat: (data: Record<string, unknown>) => axiosInstance.post('/social/formats', data),
  updateFormat: (id: string, data: Record<string, unknown>) => axiosInstance.put(`/social/formats/${id}`, data),
  deleteFormat: (id: string) => axiosInstance.delete(`/social/formats/${id}`),
}

export const meetingsApi = {
  list: (params?: Record<string, unknown>) => apiClient.get<any>('/meetings', params),
  getById: (id: string) => apiClient.get<any>(`/meetings/${id}`),
  create: (data: unknown) => apiClient.post<any>('/meetings', data),
  update: (id: string, data: unknown) => apiClient.put<any>(`/meetings/${id}`, data),
  delete: (id: string) => apiClient.delete<any>(`/meetings/${id}`),
  // Audio
  uploadAudio: (id: string, formData: FormData) =>
    axiosInstance.post(`/meetings/${id}/audio`, formData, { baseURL: UPLOAD_BASE_URL, headers: { 'Content-Type': 'multipart/form-data' } }),
  getAudioUrl: (id: string) => apiClient.get<any>(`/meetings/${id}/audio`),
  // Transcription
  transcribe: (id: string) => apiClient.post<any>(`/meetings/${id}/transcribe`),
  getSegments: (id: string) => apiClient.get<any>(`/meetings/${id}/segments`),
  mapSpeakers: (id: string, mappings: Array<{ speakerLabel: string; attendeeId: string }>) =>
    apiClient.put<any>(`/meetings/${id}/speakers`, { mappings }),
  // AI Summary
  generateSummary: (id: string) => apiClient.post<any>(`/meetings/${id}/summary`),
  toggleActionItem: (id: string, index: number) =>
    apiClient.put<any>(`/meetings/${id}/action-items/${index}`),
  // Export
  exportTranscript: (id: string, format: 'txt' | 'json') =>
    apiClient.get<any>(`/meetings/${id}/export`, { format }),
}

export const visualProductionApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/visual-production', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/visual-production/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/visual-production', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/visual-production/${id}`, data),
  delete: (id: string) =>
    axiosInstance.delete(`/visual-production/${id}`),
  transition: (id: string, data: { status: string; notes?: string }) =>
    axiosInstance.post(`/visual-production/${id}/transition`, data),
  listComments: (id: string) =>
    axiosInstance.get(`/visual-production/${id}/comments`),
  addComment: (id: string, data: { text: string; type?: string }) =>
    axiosInstance.post(`/visual-production/${id}/comments`, data),
  uploadAsset: (id: string, formData: FormData, onUploadProgress?: (e: { loaded: number; total?: number }) => void) =>
    axiosInstance.post(`/visual-production/${id}/assets`, formData, {
      baseURL: UPLOAD_BASE_URL,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...(onUploadProgress ? { onUploadProgress } : {}),
    }),
  deleteAsset: (assetId: string) =>
    axiosInstance.delete(`/visual-production/assets/${assetId}`),
  getAssetUrl: (assetId: string) =>
    axiosInstance.get(`/visual-production/assets/${assetId}/url`),
}

export const merchandisingApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/merchandising', { params }),
  getById: (id: string) =>
    axiosInstance.get(`/merchandising/${id}`),
  create: (data: unknown) =>
    axiosInstance.post('/merchandising', data),
  update: (id: string, data: unknown) =>
    axiosInstance.put(`/merchandising/${id}`, data),
  delete: (id: string) =>
    axiosInstance.delete(`/merchandising/${id}`),
  getStats: (params?: Record<string, unknown>) =>
    axiosInstance.get('/merchandising/stats', { params }),
}

// ── To-Do personale ────────────────────────────────────────
export const todoApi = {
  list: (filter?: 'all' | 'today' | 'active' | 'completed' | 'assigned') =>
    axiosInstance.get('/todos', { params: filter ? { filter } : {} }),
  count: () =>
    axiosInstance.get('/todos/count'),
  create: (data: { text: string; priority?: string; dueDate?: string | null; assignedToId?: string }) =>
    axiosInstance.post('/todos', data),
  update: (id: string, data: { text?: string; completed?: boolean; priority?: string; dueDate?: string | null; sortOrder?: number }) =>
    axiosInstance.put(`/todos/${id}`, data),
  toggle: (id: string) =>
    axiosInstance.patch(`/todos/${id}/toggle`),
  delete: (id: string) =>
    axiosInstance.delete(`/todos/${id}`),
  reorder: (orderedIds: string[]) =>
    axiosInstance.post('/todos/reorder', { orderedIds }),
  clearCompleted: () =>
    axiosInstance.delete('/todos/completed'),
}

// ── Magazzino Attrezzature ──────────────────────────────────
export const warehouseApi = {
  getOverview: () => axiosInstance.get('/warehouse/overview'),
  getUnitDetail: (id: string) => axiosInstance.get(`/warehouse/units/${id}`),
  listEquipment: (params?: Record<string, unknown>) => axiosInstance.get('/warehouse/equipment', { params }),
  getEquipment: (id: string) => axiosInstance.get(`/warehouse/equipment/${id}`),
  createEquipment: (data: unknown) => axiosInstance.post('/warehouse/equipment', data),
  updateEquipment: (id: string, data: unknown) => axiosInstance.put(`/warehouse/equipment/${id}`, data),
  deleteEquipment: (id: string) => axiosInstance.delete(`/warehouse/equipment/${id}`),
  moveEquipment: (id: string, slotId: string) => axiosInstance.patch(`/warehouse/equipment/${id}/move`, { slotId }),
  checkout: (id: string, data: { userId: string; projectId?: string; expectedReturn?: string; notes?: string }) =>
    axiosInstance.post(`/warehouse/equipment/${id}/checkout`, data),
  checkin: (checkoutId: string, data?: { returnCondition?: string; notes?: string }) =>
    axiosInstance.post(`/warehouse/checkouts/${checkoutId}/checkin`, data ?? {}),
  getActiveCheckouts: () => axiosInstance.get('/warehouse/checkouts'),
  scheduleMaintenance: (id: string, data: { type: string; scheduledDate: string; description?: string }) =>
    axiosInstance.post(`/warehouse/equipment/${id}/maintenance`, data),
  completeMaintenance: (id: string, data?: { notes?: string; cost?: number }) =>
    axiosInstance.patch(`/warehouse/maintenance/${id}/complete`, data ?? {}),
  getUpcomingMaintenance: () => axiosInstance.get('/warehouse/maintenance'),
  getCategories: () => axiosInstance.get('/warehouse/categories'),
  seed: () => axiosInstance.post('/warehouse/seed'),
  uploadPhoto: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return axiosInstance.post(`/warehouse/equipment/${id}/photo`, fd, { baseURL: UPLOAD_BASE_URL, headers: { 'Content-Type': 'multipart/form-data' } })
  },
  getQR: (id: string) => axiosInstance.get(`/warehouse/equipment/${id}/qr`),
  getLabelsUrl: (ids?: string[]) => {
    const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
    const token = localStorage.getItem('accessToken') ?? ''
    const idsParam = ids ? `&ids=${ids.join(',')}` : ''
    return `${baseUrl}/warehouse/labels?token=${encodeURIComponent(token)}${idsParam}`
  },
}

// ── Guest users ────────────────────────────────────────────
export const guestsApi = {
  list: () => apiClient.get<any>('/guests'),
  create: (data: { firstName: string; lastName: string; email: string; password: string }) =>
    apiClient.post<any>('/guests', data),
  assignProjects: (id: string, projectIds: string[]) =>
    apiClient.put<any>(`/guests/${id}/projects`, { projectIds }),
  removeProject: (id: string, projectId: string) =>
    apiClient.delete<any>(`/guests/${id}/projects/${projectId}`),
  deleteGuest: (id: string) =>
    apiClient.delete<any>(`/guests/${id}`),
  resetPassword: (id: string, password: string) =>
    apiClient.patch<any>(`/guests/${id}/password`, { password }),
  updatePedPermission: (id: string, permission: 'view' | 'approve') =>
    apiClient.patch<any>(`/guests/${id}/ped-permission`, { permission }),
}

// ── Note personali ─────────────────────────────────────────
export const notesApi = {
  list: (params?: Record<string, unknown>) =>
    axiosInstance.get('/notes', { params }),
  get: (id: string) =>
    axiosInstance.get(`/notes/${id}`),
  create: (data: Record<string, unknown>) =>
    axiosInstance.post('/notes', data),
  update: (id: string, data: Record<string, unknown>) =>
    axiosInstance.put(`/notes/${id}`, data),
  delete: (id: string) =>
    axiosInstance.delete(`/notes/${id}`),
  togglePin: (id: string) =>
    axiosInstance.patch(`/notes/${id}/pin`),
  share: (id: string, shares: Array<{ userId: string; canEdit?: boolean }>) =>
    axiosInstance.post(`/notes/${id}/share`, { shares }),
  unshare: (id: string, userId: string) =>
    axiosInstance.delete(`/notes/${id}/share/${userId}`),
  uploadAttachment: (noteId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return axiosInstance.post(`/notes/${noteId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  listAttachments: (noteId: string) => axiosInstance.get(`/notes/${noteId}/attachments`),
  downloadAttachment: (attachmentId: string) => {
    const token = localStorage.getItem('accessToken') ?? ''
    const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
    return `${baseUrl}/notes/attachments/${attachmentId}/download?token=${encodeURIComponent(token)}`
  },
  deleteAttachment: (attachmentId: string) => axiosInstance.delete(`/notes/attachments/${attachmentId}`),
}

// ============================================================
// LEADS — Richieste dal sito (CRM)
// ============================================================
export const leadsApi = {
  list: (params?: {
    status?: string;
    temperature?: string;
    assignedToId?: string;
    source?: string;
    search?: string;
    minScore?: number;
    limit?: number;
    offset?: number;
  }) => axiosInstance.get('/leads', { params }),
  get: (id: string) => axiosInstance.get(`/leads/${id}`),
  getThread: (id: string) => axiosInstance.get(`/leads/${id}/thread`),
  update: (id: string, data: Record<string, unknown>) => axiosInstance.patch(`/leads/${id}`, data),
  addActivity: (id: string, data: { type: string; title: string; content?: string }) =>
    axiosInstance.post(`/leads/${id}/activities`, data),
  convert: (id: string) => axiosInstance.post(`/leads/${id}/convert`),
  syncNow: () => axiosInstance.post('/leads/sync', {}, { timeout: 180_000 }),
  getStats: () => axiosInstance.get('/leads/stats'),
}
